import { useEffect, useState } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL - handles both old and new formats
const getImageUrl = (url) => {
  if (!url) return '';
  // Already a full URL
  if (url.startsWith('http')) {
    // Fix old format: if URL contains /uploads/ without /api/, add /api
    if (url.includes('/uploads/') && !url.includes('/api/uploads/')) {
      return url.replace('/uploads/', '/api/uploads/');
    }
    return url;
  }
  // Relative path - add BACKEND_URL
  // If path doesn't start with /api, add it
  if (url.startsWith('/uploads/')) {
    return `${BACKEND_URL}/api${url}`;
  }
  return `${BACKEND_URL}${url}`;
};

const CollectionPage = () => {
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory]);

  const fetchProducts = async () => {
    try {
      const url = selectedCategory === 'all'
        ? `${API}/products`
        : `${API}/products?category=${selectedCategory}`;
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const categories = [
    { id: 'all', label: 'All Products' },
    { id: 'extra_virgin', label: 'Extra Virgin' },
    { id: 'premium', label: 'Premium' },
    { id: 'organic', label: 'Organic' },
    { id: 'gift_boxes', label: 'Gift Boxes' },
    { id: 'accessories', label: 'Accessories' }
  ];

  return (
    <div className="min-h-screen bg-[#1A1915]">
      <Navigation />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(./images/collection.webp)',
            filter: 'brightness(0.4)'
          }}
        />
        <div className="relative z-10 text-center px-6">
          <h1 className="playfair text-5xl lg:text-7xl font-bold text-white mb-6" data-testid="collection-page-title">
            Our Collection
          </h1>
          <div className="w-32 h-1 bg-[#C9A961] mx-auto mb-6"></div>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Discover our range of premium olive oils and accessories
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-12 px-6 bg-[#221F1A] border-b border-[#C9A961]/20">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                data-testid={`filter-${category.id}`}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 border-2 transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-[#C9A961] border-[#C9A961] text-black font-semibold'
                    : 'border-[#C9A961]/30 text-gray-400 hover:border-[#C9A961] hover:text-[#C9A961]'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 px-6 bg-[#1A1915] stone-texture">
        <div className="max-w-7xl mx-auto">
          {products.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {products.map((product, index) => (
                <div
                  key={product.id}
                  data-testid={`product-item-${index}`}
                  className="group bg-[#2A2720] rounded-lg overflow-hidden border border-[#C9A961]/20 hover:border-[#C9A961] transition-all duration-300 hover:shadow-2xl hover:shadow-[#C9A961]/20"
                >
                  <div className="aspect-square overflow-hidden bg-[#3A352B]">
                    <img
                      src={getImageUrl(product.image_url)}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <span className="text-xs text-[#C9A961] uppercase tracking-wider">
                      {product.category.replace('_', ' ')}
                    </span>
                    <h3 className="playfair text-xl font-semibold text-white mt-2 mb-2">
                      {product.name}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                      {product.description}
                    </p>
                    {product.sizes && product.sizes.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs text-gray-500 mb-2">Available sizes:</p>
                        <div className="flex flex-wrap gap-2">
                          {product.sizes.map((size) => (
                            <span
                              key={size}
                              className="text-xs px-2 py-1 bg-[#C9A961]/10 text-[#C9A961] border border-[#C9A961]/30 rounded"
                            >
                              {size}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {product.price && (
                      <p className="text-[#C9A961] font-semibold text-lg mb-4">
                        ${product.price}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="text-6xl mb-6">🛒</div>
              <h3 className="playfair text-2xl text-gray-400 mb-4">
                No products found in this category
              </h3>
              <p className="text-gray-500">
                Check back soon or explore other categories
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#221F1A] border-t border-[#C9A961]/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="playfair text-3xl lg:text-4xl font-bold text-[#C9A961] mb-6">
            Interested in Partnership?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Join us in offering the world an unforgettable gustatory experience
          </p>
          <a href="/contact">
            <button className="btn-gold-filled" data-testid="partnership-cta">
              Get in Touch
            </button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CollectionPage;